import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Skeleton,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  Assignment as AssignmentIcon,
  AccountBalance as FinanceIcon,
  AdminPanelSettings as AdminIcon,
  BarChart as AnalyticsIcon,
  EventNote as MeetingsIcon,
  CalendarMonth as WeeklyIcon,
  Grading as MarksIcon,
  FactCheck as ExamsIcon,
  Groups as StaffIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Menu as MenuIcon,
  AccessTime as WorkHoursIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  Login as LoginIcon,
  Circle as CircleIcon,
  EventBusy as LeaveIcon,
  EventNote as CalendarEventIcon,
  GridOn as TimetableIcon,
  Article as ReportCardIcon,
  Inventory2 as InventoryIcon,
  ErrorOutline as OutOfStockIcon,
  Receipt as ExpenseIcon,
  ChildCare as DaycareIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { colors } from '@/theme/tokens';
import KpiCard from '@components/KpiCard';
import NeedsAttention from '@components/NeedsAttention';
import OnboardingChecklist from '@components/OnboardingChecklist';

// Per-category tile colors: { tint: chip background, accent: icon + arrow color }
const TILE_PALETTE = {
  indigo: { tint: '#E8E7FB', accent: '#4F46E5' },
  blue:   { tint: '#E6F1FB', accent: '#1D6FB8' },
  teal:   { tint: '#E1F5EE', accent: '#0F766E' },
  slate:  { tint: '#EEF1F5', accent: '#475569' },
  orange: { tint: '#FDEBDD', accent: '#C2410C' },
  green:  { tint: '#E7F4E1', accent: '#15803D' },
  amber:  { tint: '#FAEEDA', accent: '#B45309' },
  sky:    { tint: '#E0F2FE', accent: '#0369A1' },
  violet: { tint: '#F3E8FF', accent: '#7E22CE' },
  pink:   { tint: '#FCE7F0', accent: '#BE185D' },
  red:    { tint: '#FCEBEB', accent: '#B91C1C' },
};

// Brand colors — sourced from the central design tokens (theme/tokens.js).
// NOTE: BRAND.secondary is used throughout this file as a muted grey label
// color, not MUI's pink "secondary" — map it to textSecondary, not secondary.
const BRAND = {
  primary: colors.primary,
  primaryLight: colors.primaryLight,
  primaryDark: colors.primaryDark,
  secondary: colors.textSecondary,
  success: colors.success,
  warning: colors.warning,
  error: colors.error,
  cardBg: colors.bgPaper,
};

// Get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

// Format today's date
const getFormattedDate = () => {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Generate mock weekly attendance data (in real app, fetch from API)
const generateWeeklyAttendance = (todayPercent) => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date().getDay(); // 0=Sun, 1=Mon
  return days.map((day, i) => {
    if (i + 1 === today) {
      return { day, attendance: todayPercent || 0 };
    }
    if (i + 1 > today) {
      return { day, attendance: null };
    }
    // Past days: simulate around today's value
    const base = todayPercent || 65;
    const variance = Math.floor(Math.random() * 20) - 10;
    return { day, attendance: Math.max(0, Math.min(100, base + variance)) };
  });
};

// Loading Skeleton
const DashboardSkeleton = () => (
  <Box>
    <Box sx={{ mb: 4 }}>
      <Skeleton variant="text" width={300} height={40} />
      <Skeleton variant="text" width={400} height={24} />
    </Box>
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {[1, 2, 3, 4].map(i => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>
    <Skeleton variant="rounded" height={80} sx={{ borderRadius: 3, mb: 3 }} />
    <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
  </Box>
);

// Quick Action Card Component
const ActionCard = ({ title, description, icon: Icon, onClick, testId, color, stat }) => {
  const { tint, accent } = TILE_PALETTE[color] || TILE_PALETTE.indigo;
  return (
    <Card
      elevation={0}
      data-testid={testId}
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'grey.200',
        cursor: 'pointer',
        transition: 'all 0.18s ease',
        height: '100%',
        '&:hover': {
          borderColor: alpha(accent, 0.5),
          boxShadow: `0 6px 16px ${alpha('#000000', 0.08)}`,
          transform: 'translateY(-3px)',
        },
        '&:hover .tile-arrow': { opacity: 1, transform: 'translateX(0)' },
      }}
    >
      {/* Hover affordance — sits above the action area, ignores pointer events */}
      <Box
        className="tile-arrow"
        sx={{
          position: 'absolute',
          top: 14,
          right: 14,
          color: accent,
          opacity: 0,
          transform: 'translateX(-4px)',
          transition: 'all 0.18s ease',
          pointerEvents: 'none',
          zIndex: 1,
          display: 'flex',
        }}
      >
        <ArrowForwardIcon sx={{ fontSize: 18 }} />
      </Box>

      <CardActionArea onClick={onClick} sx={{ height: '100%', p: 0 }}>
        <CardContent sx={{ p: 2.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              bgcolor: tint,
              borderRadius: 2,
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 22, color: accent }} />
          </Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'grey.900' }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'grey.500', lineHeight: 1.4 }}>
            {description}
          </Typography>
          {stat && (Array.isArray(stat) ? stat : [stat]).map((line) => (
            <Box key={line} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: accent, fontWeight: 700, fontSize: '0.72rem' }}>
                {line}
              </Typography>
            </Box>
          ))}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const PrincipalDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isFeatureEnabled } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unmarkedClasses, setUnmarkedClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [inventorySummary, setInventorySummary] = useState(null);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [daycareSummary, setDaycareSummary] = useState(null);
  const [pendingLeavesCount, setPendingLeavesCount] = useState(0);
  const [attendanceTrend, setAttendanceTrend] = useState(null);
  const [hasCalendarEvents, setHasCalendarEvents] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem('classtree.onboarding.dismissed') === '1'
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isPrincipal = user?.role === 'principal';

  const dismissOnboarding = () => {
    localStorage.setItem('classtree.onboarding.dismissed', '1');
    setOnboardingDismissed(true);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
        const today = new Date().toISOString().split('T')[0];

        const [dashRes, classesRes, attendanceRes, teachersRes, financeRes, inventoryRes, expenseRes, daycareEnrollmentsRes, daycareDuesRes, daycareRosterRes, daycareNotesRes, leaveRequestsRes, attendanceTrendRes, calendarRes] = await Promise.all([
          principalAPI.getDashboard(),
          principalAPI.getClasses(),
          isPrincipal
            ? principalAPI.getStudentAttendance(today).catch(() => ({ data: { classes: [] } }))
            : Promise.resolve({ data: { classes: [] } }),
          principalAPI.getTeachers().catch(() => ({ data: { teachers: [] } })),
          isPrincipal
            ? principalAPI.getFinanceDashboard().catch(() => ({ data: { data: { summary: null } } }))
            : Promise.resolve({ data: { data: { summary: null } } }),
          principalAPI.inventory.summary().catch(() => ({ data: { data: null } })),
          isPrincipal
            ? principalAPI.expenses.summary().catch(() => ({ data: { summary: null } }))
            : Promise.resolve({ data: { summary: null } }),
          principalAPI.daycare.listEnrollments().catch(() => ({ data: { enrollments: [] } })),
          principalAPI.daycare.getDues().catch(() => ({ data: { dues: [] } })),
          principalAPI.daycare.getRoster().catch(() => ({ data: { roster: [] } })),
          principalAPI.daycare.getCareNotes().catch(() => ({ data: { notes: [] } })),
          isFeatureEnabled('leave_management')
            ? principalAPI.getLeaveRequests({ status: 'pending' }).catch(() => ({ data: { requests: [] } }))
            : Promise.resolve({ data: { requests: [] } }),
          isPrincipal
            ? principalAPI.getAttendanceAnalytics({
                start_date: sevenDaysAgo.toISOString().split('T')[0],
                end_date: today,
              }).catch(() => ({ data: { daily: null } }))
            : Promise.resolve({ data: { daily: null } }),
          isFeatureEnabled('academics')
            ? principalAPI.getCalendarEvents().catch(() => ({ data: { events: [] } }))
            : Promise.resolve({ data: { events: [] } }),
        ]);

        if (isMounted) {
          setStats(dashRes.data.stats);

          // Find classes that have no attendance marked today
          const allClasses = classesRes.data.classes || classesRes.data || [];
          const markedClassIds = new Set(
            (attendanceRes.data.classes || []).map(c => c.class?.id)
          );
          const notMarked = allClasses.filter(c => !markedClassIds.has(c.id));
          setUnmarkedClasses(notMarked);

          // Store teachers for last login display
          const teacherList = teachersRes.data.teachers || teachersRes.data || [];
          setTeachers(teacherList);

          setFinanceSummary(financeRes.data?.data?.summary || null);
          setInventorySummary(inventoryRes.data?.data || null);
          setExpenseSummary(expenseRes.data?.summary || null);

          const activeEnrollments = (daycareEnrollmentsRes.data?.enrollments || []).filter((e) => e.is_active);
          const dues = daycareDuesRes.data?.dues || [];
          const roster = daycareRosterRes.data?.roster || [];
          const careNotes = daycareNotesRes.data?.notes || [];
          setDaycareSummary({
            activeCount: activeEnrollments.length,
            checkedInCount: roster.filter((r) => r.is_checked_in).length,
            duesCount: dues.length,
            duesTotal: dues.reduce((sum, d) => sum + parseFloat(d.month_total || 0), 0),
            notesPending: careNotes.filter((n) => n.status === 'pending' || n.status === 'acknowledged').length,
            notesCompleted: careNotes.filter((n) => n.status === 'completed').length,
          });

          setPendingLeavesCount((leaveRequestsRes.data?.requests || []).length);
          setAttendanceTrend(attendanceTrendRes.data?.daily || null);
          setHasCalendarEvents((calendarRes.data?.events || []).length > 0);
        }
      } catch (error) {
        console.error('Dashboard error:', error);
        if (isMounted) {
          setStats({
            totalTeachers: 0,
            totalStudents: 0,
            totalClasses: 0,
            attendanceToday: 0,
            presentTeachers: 0,
            upcomingMeetings: 0,
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const weeklyAttendance = useMemo(
    () => generateWeeklyAttendance(stats?.attendanceToday),
    [stats?.attendanceToday]
  );

  // Real 7-day attendance % series (not the mock weeklyAttendance above) for the KPI sparkline.
  const attendanceSpark = useMemo(
    () => (attendanceTrend || []).map((d) => d.percentage),
    [attendanceTrend]
  );
  const attendanceSparkTrend = attendanceSpark.length >= 2
    ? (attendanceSpark.at(-1) > attendanceSpark.at(-2) ? 'up' : attendanceSpark.at(-1) < attendanceSpark.at(-2) ? 'down' : null)
    : null;

  const attentionItems = [
    {
      label: 'Fees pending',
      count: financeSummary?.totalPending || 0,
      display: `₹${Math.round(financeSummary?.totalPending || 0).toLocaleString('en-IN')}`,
      severity: 'error',
      href: '/principal/finance',
    },
    {
      label: 'Leaves to approve',
      count: pendingLeavesCount,
      severity: 'warning',
      href: '/principal/leave-management',
    },
  ];

  const onboardingSteps = [
    { key: 'classes', label: 'Add your first class', description: 'Create classes & sections', done: (stats?.totalClasses || 0) > 0, href: '/principal/classes', cta: 'Add class' },
    { key: 'teachers', label: 'Add teachers', description: 'Invite teaching staff', done: (stats?.totalTeachers || 0) > 0, href: '/principal/teachers', cta: 'Add teacher' },
    { key: 'students', label: 'Add students', description: 'Enroll students into classes', done: (stats?.totalStudents || 0) > 0, href: '/principal/students', cta: 'Add student' },
    { key: 'fees', label: 'Set up fee structures', description: 'Define class fees & due dates', done: Boolean(financeSummary?.totalFees > 0), href: '/principal/finance', cta: 'Set up fees' },
    ...(isFeatureEnabled('academics') ? [
      { key: 'calendar', label: 'Add school events', description: 'Holidays, exams & key dates', done: hasCalendarEvents, href: '/principal/calendar', cta: 'Add events' },
    ] : []),
  ];

  const daycareStat = (daycareSummary && daycareSummary.activeCount > 0)
    ? [
        `${daycareSummary.activeCount} enrolled · ${daycareSummary.checkedInCount} checked in`,
        (daycareSummary.notesPending > 0 || daycareSummary.notesCompleted > 0)
          ? `${daycareSummary.notesPending} note${daycareSummary.notesPending === 1 ? '' : 's'} pending · ${daycareSummary.notesCompleted} done`
          : null,
        daycareSummary.duesCount > 0
          ? `₹${Math.round(daycareSummary.duesTotal).toLocaleString('en-IN')} dues outstanding`
          : null,
      ].filter(Boolean)
    : null;

  // Manage actions - icon cards
  const manageActions = [
    { title: 'Teachers', description: 'Add, edit & view teacher profiles', icon: PeopleIcon, href: '/principal/teachers', testId: 'manage-teachers-button', color: 'indigo' },
    { title: 'Students', description: 'Manage student enrollment & details', icon: SchoolIcon, href: '/principal/students', testId: 'manage-students-button', color: 'blue' },
    { title: 'Classes', description: 'Create and organize class sections', icon: ClassIcon, href: '/principal/classes', testId: 'manage-classes-button', color: 'teal' },
    { title: 'Non-Teaching Staff', description: 'Manage support staff records', icon: StaffIcon, href: '/principal/non-teaching-staff', testId: 'manage-non-teaching-staff-button', color: 'slate' },
    { title: 'Exams', description: 'Set up and manage examinations', icon: ExamsIcon, href: '/principal/exams', testId: 'manage-exams-button', featureKey: 'academics', color: 'orange' },
    { title: 'Finance', description: 'Fees collection & payment tracking', icon: FinanceIcon, href: '/principal/finance', testId: 'manage-finance-button', color: 'green' },
    { title: 'Leave Management', description: 'Teacher leave balances & requests', icon: LeaveIcon, href: '/principal/leave-management', testId: 'manage-leaves-button', featureKey: 'leave_management', color: 'amber' },
    { title: 'Academic Calendar', description: 'School events, holidays & exam dates', icon: CalendarEventIcon, href: '/principal/calendar', testId: 'manage-calendar-button', featureKey: 'academics', color: 'sky' },
    { title: 'Class Timetable', description: 'Build period-wise class schedules', icon: TimetableIcon, href: '/principal/timetable', testId: 'manage-timetable-button', featureKey: 'academics', color: 'violet' },
    { title: 'Daycare', description: 'Enrollments, sessions & billing', icon: DaycareIcon, href: '/principal/daycare', testId: 'manage-daycare-button', color: 'pink', stat: daycareStat },
  ].filter((action) => !action.featureKey || isFeatureEnabled(action.featureKey));

  if (isPrincipal) {
    manageActions.push({
      title: 'School Admins',
      description: 'Manage admin accounts & access',
      icon: AdminIcon,
      href: '/principal/school-admins',
      testId: 'manage-school-admins-button',
      color: 'red',
    });
  }

  const viewActions = (isPrincipal ? [
    { title: 'Analytics', description: 'School performance insights', icon: AnalyticsIcon, href: '/principal/analytics', testId: 'view-analytics-button', color: 'indigo' },
    { title: 'Attendance', description: 'Daily attendance tracking', icon: AssignmentIcon, href: '/principal/attendance', testId: 'view-attendance-button', color: 'teal' },
    { title: 'Parent Meetings', description: 'Schedule & track meetings', icon: MeetingsIcon, href: '/principal/parent-meetings', testId: 'parent-meetings-button', color: 'pink' },
    { title: 'Weekly Plans', description: 'View teacher weekly plans', icon: WeeklyIcon, href: '/principal/weekly-reports', testId: 'weekly-reports-button', featureKey: 'academics', color: 'amber' },
    { title: 'Marks', description: 'View exam results & marks', icon: MarksIcon, href: '/principal/view-marks', testId: 'view-marks-button', featureKey: 'academics', color: 'orange' },
    { title: 'Report Cards', description: 'Generate & publish student report cards', icon: ReportCardIcon, href: '/principal/report-cards', testId: 'manage-report-cards-button', featureKey: 'academics', color: 'green' },
    { title: 'Work Hours', description: 'Teacher check-in/out records', icon: WorkHoursIcon, href: '/principal/teachers/work-hours', testId: 'view-work-hours-button', color: 'blue' },
  ] : []).filter((action) => !action.featureKey || isFeatureEnabled(action.featureKey));

  return (
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: '#F8FAFC',
            p: { xs: 2, sm: 3, md: 4 },
          }}
          data-testid="principal-dashboard"
        >
          {loading ? (
            <DashboardSkeleton />
          ) : (
            <>
              {/* Greeting - Change 5 */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: 'grey.900', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {getGreeting()}, {user?.name?.split(' ')[0]}
                </Typography>
                <Typography variant="body2" sx={{ color: 'grey.500', mt: 0.25 }}>
                  {isPrincipal
                    ? `Here's what's happening at ${user?.school?.name || 'your school'} today.`
                    : 'Ready to manage fees and student records today.'}
                </Typography>
              </Box>

              {isPrincipal && !onboardingDismissed && (
                <OnboardingChecklist steps={onboardingSteps} onDismiss={dismissOnboarding} />
              )}

              <NeedsAttention items={attentionItems} />

              {/* Stat Cards - Change 1 & 2 */}
              <Grid container spacing={2.5} sx={{ mb: 3 }} data-testid="stats-section">
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    title="Total Teachers"
                    value={stats?.totalTeachers || 0}
                    trendLabel={`${stats?.presentTeachers || 0} present today`}
                    trend={stats?.presentTeachers >= stats?.totalTeachers ? 'up' : 'down'}
                    icon={PeopleIcon}
                    accent={BRAND.primary}
                    onClick={() => navigate('/principal/teachers')}
                    testId="stat-teachers"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    title="Total Students"
                    value={stats?.totalStudents || 0}
                    trendLabel="View student list"
                    icon={SchoolIcon}
                    accent="#7C3AED"
                    onClick={() => navigate('/principal/students')}
                    testId="stat-students"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    title="Total Classes"
                    value={stats?.totalClasses || 0}
                    trendLabel="Manage sections"
                    icon={ClassIcon}
                    accent="#0891B2"
                    onClick={() => navigate('/principal/classes')}
                    testId="stat-classes"
                  />
                </Grid>
                {isPrincipal && (
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      title="Attendance Today"
                      value={`${stats?.attendanceToday || 0}%`}
                      trendLabel={
                        stats?.attendanceToday >= 75
                          ? 'Good attendance rate'
                          : stats?.attendanceToday >= 50
                            ? 'Below average'
                            : 'Needs attention'
                      }
                      trend={attendanceSparkTrend}
                      spark={attendanceSpark}
                      icon={AssignmentIcon}
                      accent={
                        stats?.attendanceToday >= 75
                          ? BRAND.success
                          : stats?.attendanceToday >= 50
                            ? BRAND.warning
                            : BRAND.error
                      }
                      onClick={() => navigate('/principal/attendance')}
                      testId="stat-attendance"
                    />
                  </Grid>
                )}
              </Grid>

              {/* Today at a Glance - Change 4 */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: BRAND.cardBg,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ color: 'grey.900', mb: 2, fontSize: '0.9rem' }}
                >
                  Today at a Glance — {getFormattedDate()}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckIcon sx={{ color: BRAND.success, fontSize: 22 }} />
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        <strong>{stats?.presentTeachers || 0}</strong> of{' '}
                        <strong>{stats?.totalTeachers || 0}</strong> teachers present
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <ScheduleIcon sx={{ color: BRAND.primary, fontSize: 22 }} />
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        <strong>{stats?.upcomingMeetings || 0}</strong> upcoming parent{' '}
                        {stats?.upcomingMeetings === 1 ? 'meeting' : 'meetings'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AssignmentIcon
                        sx={{
                          color:
                            stats?.attendanceToday >= 75
                              ? BRAND.success
                              : stats?.attendanceToday >= 50
                                ? BRAND.warning
                                : BRAND.error,
                          fontSize: 22,
                        }}
                      />
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        <strong>{stats?.attendanceToday || 0}%</strong> student attendance today
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Finance Overview */}
              {isPrincipal && financeSummary && (financeSummary.totalFees > 0 || financeSummary.totalCollected > 0) && (
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'grey.200', bgcolor: BRAND.cardBg, cursor: 'pointer' }}
                  onClick={() => navigate('/principal/finance')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FinanceIcon sx={{ color: BRAND.primary, fontSize: 22 }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'grey.900', fontSize: '0.9rem' }}>
                        Finance Overview
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                      onClick={(e) => { e.stopPropagation(); navigate('/principal/finance'); }}
                      sx={{ color: BRAND.primary, fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', '&:hover': { bgcolor: alpha(BRAND.primary, 0.08) } }}
                    >
                      Manage
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1, bgcolor: alpha(BRAND.success, 0.08), borderRadius: 2, display: 'flex' }}>
                          <FinanceIcon sx={{ fontSize: 20, color: BRAND.success }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Collected</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.success }}>
                            ₹{(financeSummary.totalCollected || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1, bgcolor: alpha(BRAND.warning, 0.08), borderRadius: 2, display: 'flex' }}>
                          <FinanceIcon sx={{ fontSize: 20, color: BRAND.warning }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Outstanding</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.warning }}>
                            ₹{(financeSummary.totalPending || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1, bgcolor: alpha(BRAND.primary, 0.08), borderRadius: 2, display: 'flex' }}>
                          <FinanceIcon sx={{ fontSize: 20, color: BRAND.primary }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Collection Rate</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.primary }}>
                            {financeSummary.totalFees > 0 ? `${Math.round((financeSummary.totalCollected / financeSummary.totalFees) * 100)}%` : '0%'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Inventory Alerts */}
              {inventorySummary && parseInt(inventorySummary.total_items, 10) > 0 &&
               (parseInt(inventorySummary.low_stock_count, 10) > 0 || parseInt(inventorySummary.out_of_stock_count, 10) > 0) && (
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: alpha(BRAND.warning, 0.4), bgcolor: alpha(BRAND.warning, 0.04), cursor: 'pointer' }}
                  onClick={() => navigate('/principal/inventory?low_stock=true')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InventoryIcon sx={{ color: BRAND.warning, fontSize: 22 }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'grey.900', fontSize: '0.9rem' }}>
                        Inventory Alerts
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                      onClick={(e) => { e.stopPropagation(); navigate('/principal/inventory?low_stock=true'); }}
                      sx={{ color: BRAND.warning, fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', '&:hover': { bgcolor: alpha(BRAND.warning, 0.08) } }}
                    >
                      View All
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {parseInt(inventorySummary.low_stock_count, 10) > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ p: 0.75, bgcolor: alpha(BRAND.warning, 0.12), borderRadius: 1.5, display: 'flex' }}>
                          <WarningIcon sx={{ fontSize: 18, color: BRAND.warning }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Low Stock</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.warning }}>{inventorySummary.low_stock_count} items</Typography>
                        </Box>
                      </Box>
                    )}
                    {parseInt(inventorySummary.out_of_stock_count, 10) > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ p: 0.75, bgcolor: alpha(BRAND.error, 0.12), borderRadius: 1.5, display: 'flex' }}>
                          <OutOfStockIcon sx={{ fontSize: 18, color: BRAND.error }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Out of Stock</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.error }}>{inventorySummary.out_of_stock_count} items</Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              )}

              {/* Expense Reimbursements Widget */}
              {isPrincipal && expenseSummary && parseInt(expenseSummary.pending_count, 10) > 0 && (
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: alpha(BRAND.primary, 0.35), bgcolor: alpha(BRAND.primaryLight, 0.6), cursor: 'pointer' }}
                  onClick={() => navigate('/principal/expenses')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ExpenseIcon sx={{ color: BRAND.primary, fontSize: 22 }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'grey.900', fontSize: '0.9rem' }}>
                        Reimbursements Pending
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                      onClick={(e) => { e.stopPropagation(); navigate('/principal/expenses'); }}
                      sx={{ color: BRAND.primary, fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', '&:hover': { bgcolor: alpha(BRAND.primary, 0.08) } }}
                    >
                      Review
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ p: 0.75, bgcolor: alpha(BRAND.primary, 0.12), borderRadius: 1.5, display: 'flex' }}>
                        <ExpenseIcon sx={{ fontSize: 18, color: BRAND.primary }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Claims</Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.primary }}>{expenseSummary.pending_count}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Amount</Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.primary }}>
                          ₹{parseFloat(expenseSummary.pending_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              )}

              {/* Daycare Widget */}
              {daycareSummary && daycareSummary.activeCount > 0 && (
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: alpha(TILE_PALETTE.pink.accent, 0.35), bgcolor: alpha(TILE_PALETTE.pink.tint, 0.6), cursor: 'pointer' }}
                  onClick={() => navigate('/principal/daycare')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DaycareIcon sx={{ color: TILE_PALETTE.pink.accent, fontSize: 22 }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'grey.900', fontSize: '0.9rem' }}>
                        Daycare
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                      onClick={(e) => { e.stopPropagation(); navigate('/principal/daycare'); }}
                      sx={{ color: TILE_PALETTE.pink.accent, fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', '&:hover': { bgcolor: alpha(TILE_PALETTE.pink.accent, 0.08) } }}
                    >
                      Manage
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                        p: 1, pr: 1.5, borderRadius: 2, border: '1px solid', borderColor: alpha(TILE_PALETTE.pink.accent, 0.25),
                        bgcolor: 'background.paper', transition: 'all 0.15s',
                        '&:hover': { bgcolor: alpha(TILE_PALETTE.pink.accent, 0.1), borderColor: TILE_PALETTE.pink.accent, transform: 'translateY(-1px)' },
                      }}
                      onClick={(e) => { e.stopPropagation(); navigate('/principal/daycare', { state: { initialTab: 3 } }); }}
                    >
                      <Box sx={{ p: 0.75, bgcolor: alpha(TILE_PALETTE.pink.accent, 0.12), borderRadius: 1.5, display: 'flex' }}>
                        <DaycareIcon sx={{ fontSize: 18, color: TILE_PALETTE.pink.accent }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Enrolled</Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color: TILE_PALETTE.pink.accent }}>{daycareSummary.activeCount} students</Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                        p: 1, pr: 1.5, borderRadius: 2, border: '1px solid', borderColor: alpha(BRAND.success, 0.25),
                        bgcolor: 'background.paper', transition: 'all 0.15s',
                        '&:hover': { bgcolor: alpha(BRAND.success, 0.1), borderColor: BRAND.success, transform: 'translateY(-1px)' },
                      }}
                      onClick={(e) => { e.stopPropagation(); navigate('/principal/daycare', { state: { initialTab: 0 } }); }}
                    >
                      <Box sx={{ p: 0.75, bgcolor: alpha(BRAND.success, 0.12), borderRadius: 1.5, display: 'flex' }}>
                        <CheckIcon sx={{ fontSize: 18, color: BRAND.success }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Checked In Now</Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.success }}>{daycareSummary.checkedInCount} students</Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                        p: 1, pr: 1.5, borderRadius: 2, border: '1px solid', borderColor: alpha(TILE_PALETTE.pink.accent, 0.25),
                        bgcolor: 'background.paper', transition: 'all 0.15s',
                        '&:hover': { bgcolor: alpha(TILE_PALETTE.pink.accent, 0.1), borderColor: TILE_PALETTE.pink.accent, transform: 'translateY(-1px)' },
                      }}
                      onClick={(e) => { e.stopPropagation(); navigate('/principal/daycare', { state: { initialTab: 4 } }); }}
                    >
                      <Box sx={{ p: 0.75, bgcolor: alpha(TILE_PALETTE.pink.accent, 0.12), borderRadius: 1.5, display: 'flex' }}>
                        <PersonIcon sx={{ fontSize: 18, color: TILE_PALETTE.pink.accent }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Student Profile</Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color: TILE_PALETTE.pink.accent }}>View details</Typography>
                      </Box>
                    </Box>
                    {(daycareSummary.notesPending > 0 || daycareSummary.notesCompleted > 0) && (
                      <Box
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                          p: 1, pr: 1.5, borderRadius: 2, border: '1px solid', borderColor: alpha(BRAND.warning, 0.25),
                          bgcolor: 'background.paper', transition: 'all 0.15s',
                          '&:hover': { bgcolor: alpha(BRAND.warning, 0.1), borderColor: BRAND.warning, transform: 'translateY(-1px)' },
                        }}
                        onClick={(e) => { e.stopPropagation(); navigate('/principal/daycare', { state: { initialTab: 1 } }); }}
                      >
                        <Box sx={{ p: 0.75, bgcolor: alpha(BRAND.warning, 0.12), borderRadius: 1.5, display: 'flex' }}>
                          <WarningIcon sx={{ fontSize: 18, color: BRAND.warning }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Care Notes Today</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.warning }}>
                            {daycareSummary.notesPending} pending · {daycareSummary.notesCompleted} done
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {daycareSummary.duesCount > 0 && (
                      <Box
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                          p: 1, pr: 1.5, borderRadius: 2, border: '1px solid', borderColor: alpha(TILE_PALETTE.pink.accent, 0.25),
                          bgcolor: 'background.paper', transition: 'all 0.15s',
                          '&:hover': { bgcolor: alpha(TILE_PALETTE.pink.accent, 0.1), borderColor: TILE_PALETTE.pink.accent, transform: 'translateY(-1px)' },
                        }}
                        onClick={(e) => { e.stopPropagation(); navigate('/principal/daycare', { state: { initialTab: 5 } }); }}
                      >
                        <Box sx={{ p: 0.75, bgcolor: alpha(TILE_PALETTE.pink.accent, 0.12), borderRadius: 1.5, display: 'flex' }}>
                          <MoneyIcon sx={{ fontSize: 18, color: TILE_PALETTE.pink.accent }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Outstanding Dues</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: TILE_PALETTE.pink.accent }}>
                            ₹{daycareSummary.duesTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              )}

              {/* Attendance Not Marked Card */}
              {isPrincipal && unmarkedClasses.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    border: '1px solid',
                    borderColor: alpha(BRAND.warning, 0.4),
                    bgcolor: alpha(BRAND.warning, 0.04),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon sx={{ color: BRAND.warning, fontSize: 22 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: 'grey.900', fontSize: '0.9rem' }}
                      >
                        Attendance Not Marked
                      </Typography>
                      <Chip
                        label={`${unmarkedClasses.length} ${unmarkedClasses.length === 1 ? 'class' : 'classes'}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(BRAND.warning, 0.15),
                          color: '#92400E',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 22,
                        }}
                      />
                    </Box>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                      onClick={() => navigate('/principal/attendance')}
                      sx={{
                        color: BRAND.warning,
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        '&:hover': { bgcolor: alpha(BRAND.warning, 0.08) },
                      }}
                    >
                      View All
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    {unmarkedClasses.map((cls) => (
                      <Grid item xs={6} sm={4} md={3} key={cls.id}>
                        <Paper
                          elevation={0}
                          onClick={() => navigate('/principal/attendance')}
                          sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: 'grey.200',
                            bgcolor: '#fff',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              borderColor: alpha(BRAND.warning, 0.5),
                              boxShadow: `0 2px 8px ${alpha(BRAND.warning, 0.12)}`,
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 2,
                              bgcolor: alpha(BRAND.warning, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <ClassIcon sx={{ fontSize: 18, color: BRAND.warning }} />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                color: 'grey.900',
                                fontSize: '0.8rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {cls.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'grey.500',
                                fontSize: '0.7rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                              }}
                            >
                              {cls.teacher?.user?.name || cls.teacher?.name || 'No teacher assigned'}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}

              {/* All classes marked — success state */}
              {isPrincipal && !loading && unmarkedClasses.length === 0 && stats?.totalClasses > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    border: '1px solid',
                    borderColor: alpha(BRAND.success, 0.3),
                    bgcolor: alpha(BRAND.success, 0.04),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <CheckIcon sx={{ color: BRAND.success, fontSize: 22 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#065F46' }}>
                    All classes have marked attendance for today
                  </Typography>
                </Paper>
              )}

              {/* Attendance Chart - Change 6 */}
              {isPrincipal && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: BRAND.cardBg,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: 'grey.900', mb: 2, fontSize: '0.9rem' }}
                  >
                    Attendance This Week
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={weeklyAttendance} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis
                        dataKey="day"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#94A3B8' }}
                      />
                      <YAxis
                        domain={[0, 100]}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#94A3B8' }}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip
                        formatter={(value) => [`${value}%`, 'Attendance']}
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid #E2E8F0',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          fontSize: 13,
                        }}
                      />
                      <Bar
                        dataKey="attendance"
                        fill={BRAND.primary}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={48}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              )}

              {/* Teacher Last Login */}
              {isPrincipal && teachers.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: BRAND.cardBg,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LoginIcon sx={{ color: BRAND.primary, fontSize: 22 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: 'grey.900', fontSize: '0.9rem' }}
                      >
                        Teacher Last Login
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                      onClick={() => navigate('/principal/teachers')}
                      sx={{
                        color: BRAND.primary,
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        '&:hover': { bgcolor: alpha(BRAND.primary, 0.08) },
                      }}
                    >
                      All Teachers
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    {teachers.map((teacher) => {
                      const lastLogin = teacher.user?.last_login;
                      const isRecent = lastLogin && (Date.now() - new Date(lastLogin).getTime()) < 24 * 60 * 60 * 1000;
                      const isStale = lastLogin && (Date.now() - new Date(lastLogin).getTime()) > 7 * 24 * 60 * 60 * 1000;

                      const formatLastLogin = (dateStr) => {
                        if (!dateStr) return 'Never logged in';
                        const date = new Date(dateStr);
                        const now = new Date();
                        const diffMs = now - date;
                        const diffMins = Math.floor(diffMs / 60000);
                        const diffHours = Math.floor(diffMs / 3600000);
                        const diffDays = Math.floor(diffMs / 86400000);

                        if (diffMins < 1) return 'Just now';
                        if (diffMins < 60) return `${diffMins}m ago`;
                        if (diffHours < 24) return `${diffHours}h ago`;
                        if (diffDays < 7) return `${diffDays}d ago`;
                        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                      };

                      return (
                        <Grid item xs={6} sm={4} md={3} key={teacher.id}>
                          <Paper
                            elevation={0}
                            onClick={() => navigate('/principal/teachers')}
                            sx={{
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              cursor: 'pointer',
                              border: '1px solid',
                              borderColor: 'grey.200',
                              bgcolor: '#fff',
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                borderColor: alpha(BRAND.primary, 0.4),
                                boxShadow: `0 2px 8px ${alpha(BRAND.primary, 0.08)}`,
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: !lastLogin
                                  ? alpha(BRAND.secondary, 0.1)
                                  : isRecent
                                    ? alpha(BRAND.success, 0.1)
                                    : isStale
                                      ? alpha(BRAND.error, 0.1)
                                      : alpha(BRAND.warning, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                position: 'relative',
                              }}
                            >
                              <PeopleIcon
                                sx={{
                                  fontSize: 18,
                                  color: !lastLogin
                                    ? BRAND.secondary
                                    : isRecent
                                      ? BRAND.success
                                      : isStale
                                        ? BRAND.error
                                        : BRAND.warning,
                                }}
                              />
                              <CircleIcon
                                sx={{
                                  fontSize: 8,
                                  position: 'absolute',
                                  bottom: 1,
                                  right: 1,
                                  color: !lastLogin
                                    ? BRAND.secondary
                                    : isRecent
                                      ? BRAND.success
                                      : isStale
                                        ? BRAND.error
                                        : BRAND.warning,
                                }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  color: 'grey.900',
                                  fontSize: '0.8rem',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {teacher.user?.name || teacher.name || 'Unknown'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: !lastLogin
                                    ? BRAND.secondary
                                    : isRecent
                                      ? BRAND.success
                                      : isStale
                                        ? BRAND.error
                                        : BRAND.warning,
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                }}
                              >
                                {formatLastLogin(lastLogin)}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              )}

              {/* Quick Actions - Manage Section - Change 1 & 3 */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: BRAND.cardBg,
                }}
                data-testid="manage-section"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ color: 'grey.900', mb: 2, fontSize: '0.9rem' }}
                >
                  Manage
                </Typography>
                <Grid container spacing={2}>
                  {manageActions.map((action) => (
                    <Grid item xs={6} sm={4} md={3} key={action.title}>
                      <ActionCard
                        title={action.title}
                        description={action.description}
                        icon={action.icon}
                        onClick={() => navigate(action.href)}
                        testId={action.testId}
                        color={action.color}
                        stat={action.stat}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {/* Quick Actions - View & Reports Section */}
              {isPrincipal && viewActions.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: BRAND.cardBg,
                  }}
                  data-testid="view-section"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: 'grey.900', mb: 2, fontSize: '0.9rem' }}
                  >
                    View & Reports
                  </Typography>
                  <Grid container spacing={2}>
                    {viewActions.map((action) => (
                      <Grid item xs={6} sm={4} md={3} key={action.title}>
                        <ActionCard
                          title={action.title}
                          description={action.description}
                          icon={action.icon}
                          onClick={() => navigate(action.href)}
                          testId={action.testId}
                          color={action.color}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}
            </>
          )}
        </Box>
  );
};

export default PrincipalDashboard;
